/**
 * @file     sharedContent.ts
 * @purpose  i18n schema for the unified property detail view. Source-agnostic
 *           strings live under pages.propertyDetail.shared.*, used by both
 *           /properties/<curated-slug> and /properties/<mls-slug>.
 */

import { localizedText } from '@/core/content';

export const listingDetailContent = {
  hero: {
    bedsOne: localizedText('pages.propertyDetail.shared.hero.bedsOne', '{{count}} bed'),
    bedsMany: localizedText('pages.propertyDetail.shared.hero.bedsMany', '{{count}} beds'),
    bathsOne: localizedText('pages.propertyDetail.shared.hero.bathsOne', '{{count}} bath'),
    bathsMany: localizedText('pages.propertyDetail.shared.hero.bathsMany', '{{count}} baths'),
    sqft: localizedText('pages.propertyDetail.shared.hero.sqft', '{{sqft}} sq ft'),
    yearBuilt: localizedText('pages.propertyDetail.shared.hero.yearBuilt', 'Built {{year}}'),
    statusPending: localizedText('pages.propertyDetail.shared.hero.statusPending', 'Pending'),
    statusSold: localizedText('pages.propertyDetail.shared.hero.statusSold', 'Sold'),
    statusComingSoon: localizedText(
      'pages.propertyDetail.shared.hero.statusComingSoon',
      'Coming soon',
    ),
    heroPhotoAlt: localizedText(
      'pages.propertyDetail.shared.hero.heroPhotoAlt',
      'Photo of {{address}}',
    ),
  },
  cta: {
    financing: localizedText(
      'pages.propertyDetail.shared.cta.financing',
      'Information on financing',
    ),
    savePdf: localizedText('pages.propertyDetail.shared.cta.savePdf', 'Save PDF'),
    inquire: localizedText('pages.propertyDetail.shared.cta.inquire', 'Inquire'),
  },
  stickyBar: {
    a11yLabel: localizedText(
      'pages.propertyDetail.shared.stickyBar.a11yLabel',
      'Quick actions for this listing',
    ),
    financing: localizedText(
      'pages.propertyDetail.shared.stickyBar.financing',
      'Financing',
    ),
    inquire: localizedText('pages.propertyDetail.shared.stickyBar.inquire', 'Inquire'),
  },
  summary: {
    beds: localizedText('pages.propertyDetail.shared.summary.beds', 'Beds'),
    baths: localizedText('pages.propertyDetail.shared.summary.baths', 'Baths'),
    sqft: localizedText('pages.propertyDetail.shared.summary.sqft', 'Sqft'),
    lot: localizedText('pages.propertyDetail.shared.summary.lot', 'Lot size'),
    built: localizedText('pages.propertyDetail.shared.summary.built', 'Built'),
    specsLabel: localizedText('pages.propertyDetail.shared.summary.specsLabel', 'Key facts'),
    badgesLabel: localizedText('pages.propertyDetail.shared.summary.badgesLabel', 'Listing status'),
    perSqft: localizedText('pages.propertyDetail.shared.summary.perSqft', '{{price}}/sqft'),
    listedBy: localizedText('pages.propertyDetail.shared.summary.listedBy', 'Listed by {{office}}'),
    mlsNumber: localizedText('pages.propertyDetail.shared.summary.mlsNumber', 'MLS® {{id}}'),
    askingPrice: localizedText('pages.propertyDetail.shared.summary.askingPrice', 'Asking price'),
    lastAskingPrice: localizedText('pages.propertyDetail.shared.summary.lastAskingPrice', 'Last asking price'),
    priceDown: localizedText('pages.propertyDetail.shared.summary.priceDown', 'Down {{amount}}'),
    priceUp: localizedText('pages.propertyDetail.shared.summary.priceUp', 'Up {{amount}}'),
    originalPrice: localizedText('pages.propertyDetail.shared.summary.originalPrice', 'Was {{price}}'),
    vowLine: localizedText(
      'pages.propertyDetail.shared.summary.vowLine',
      'MLS® VOW data. For your personal, non-commercial use under the VOW Data Terms of Use.',
    ),
    monthly: localizedText(
      'pages.propertyDetail.shared.summary.monthly',
      '{{amount}}/mo principal and interest at {{rate}}',
    ),
    monthlyShort: localizedText('pages.propertyDetail.shared.summary.monthlyShort', '{{amount}}/mo at {{rate}}'),
    monthlyA11y: localizedText(
      'pages.propertyDetail.shared.summary.monthlyA11y',
      '{{amount}} a month principal and interest at {{rate}}. Shows the monthly costs.',
    ),
    openHouseWhen: localizedText('pages.propertyDetail.shared.summary.openHouseWhen', '{{day}}, {{start}} to {{end}}'),
  },
  actions: {
    addToList: localizedText('pages.propertyDetail.shared.actions.addToList', 'Add to list'),
    onYourList: localizedText('pages.propertyDetail.shared.actions.onYourList', 'On your list'),
    compare: localizedText('pages.propertyDetail.shared.actions.compare', 'Compare'),
    inCompare: localizedText('pages.propertyDetail.shared.actions.inCompare', 'In compare'),
    compareFull: localizedText('pages.propertyDetail.shared.actions.compareFull', 'Compare holds 4 homes'),
    share: localizedText('pages.propertyDetail.shared.actions.share', 'Share'),
    more: localizedText('pages.propertyDetail.shared.actions.more', 'More actions'),
    brokerHint: localizedText('pages.propertyDetail.shared.actions.brokerHint', 'Independent mortgage broker'),
    groupLabel: localizedText('pages.propertyDetail.shared.actions.groupLabel', 'Actions for this listing'),
  },
} as const;
